import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { Card, CardHeader, EmptyState, Spinner, Tabs } from '@opendate-app/setlist';
import ChartTooltip from '../ChartTooltip';
import { SERIES, formatCount } from './bookingUtils';

const METRIC_TABS = Object.entries(SERIES).map(([value, { label }]) => ({ value, label }));
const GRANULARITY_TABS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];
const COMPARE_COLOR = '#A4A7AE';
const AXIS_TICK = { fontSize: 12, fill: 'var(--sl-text-tertiary)' };
const TOOLTIP_ENTRIES = [
  { dataKey: 'compare', label: 'Last year', format: formatCount },
  { dataKey: 'current', label: 'This period', format: formatCount },
];
const renderTooltip = (props) => <ChartTooltip {...props} entries={TOOLTIP_ENTRIES} labelFormatter={(label) => label} />;

const monthLabel = (iso, withYear) => {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString('en-US', withYear ? { month: 'short', year: '2-digit' } : { month: 'short' });
};

export function buildTrendSeries(trend, metric, granularity) {
  const months = trend?.months || [];
  const lastYear = trend?.last_year_months || [];

  if (granularity === 'year') {
    return (trend?.years || []).map((year) => ({ label: year.label, current: year[metric] }));
  }

  const spansYears = months.length > 0 && months[0].month.slice(0, 4) !== months[months.length - 1].month.slice(0, 4);
  const rows = months.map((row, i) => ({
    label: monthLabel(row.month, spansYears),
    quarter: `Q${Math.floor((Number(row.month.slice(5, 7)) - 1) / 3) + 1} ${row.month.slice(2, 4)}`,
    current: row[metric],
    compare: lastYear[i] ? lastYear[i][metric] : null,
  }));
  if (granularity === 'month') return rows;

  const quarters = [];
  rows.forEach((row) => {
    const last = quarters[quarters.length - 1];
    if (last && last.label === row.quarter) {
      last.current += row.current;
      last.compare += row.compare || 0;
    } else {
      quarters.push({ label: row.quarter, current: row.current, compare: row.compare || 0 });
    }
  });
  return quarters;
}

export function trendHeadline(trend, metric) {
  const current = (trend?.months || []).reduce((sum, row) => sum + row[metric], 0);
  const base = (trend?.last_year_months || []).reduce((sum, row) => sum + row[metric], 0);
  const name = SERIES[metric].label;
  if (!base) return `${formatCount(current)} ${name.toLowerCase()} in this period.`;
  const pct = ((current - base) / base) * 100;
  const dir = pct >= 0 ? 'up' : 'down';
  return `${name} are ${dir} ${Math.abs(pct).toFixed(1)}% vs the same period last year (${formatCount(base)} → ${formatCount(current)}).`;
}

function BookingTrendCard({ trend, loading, error }) {
  const [metric, setMetric] = useState('holds');
  const [granularity, setGranularity] = useState('month');
  const data = useMemo(() => buildTrendSeries(trend, metric, granularity), [trend, metric, granularity]);
  const color = SERIES[metric].color;
  const hasData = data.some((row) => row.current || row.compare);

  const actions = (
    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={METRIC_TABS} value={metric} onChange={setMetric} />
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={GRANULARITY_TABS} value={granularity} onChange={setGranularity} />
    </div>
  );

  let body;
  if (loading) {
    body = <div className="d-flex justify-content-center py-5"><Spinner label="Loading…" /></div>;
  } else if (error) {
    body = <EmptyState title={error} />;
  } else if (!hasData) {
    body = <EmptyState title="No booking activity in this range" description="Try a wider date range or fewer filters." />;
  } else {
    const common = { data, margin: { top: 8, right: 8, left: 0, bottom: 0 } };
    const axes = [
      <CartesianGrid key="grid" vertical={false} stroke="var(--sl-border-secondary)" />,
      <XAxis key="x" dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />,
      <YAxis key="y" tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} allowDecimals={false} />,
      <Tooltip
        key="tip"
        content={renderTooltip}
        cursor={granularity === 'month' ? { stroke: 'var(--sl-border-primary)' } : { fill: 'var(--sl-bg-secondary)' }}
      />,
    ];
    const compareName = granularity === 'year' ? null : 'Last year';
    body = (
      <ResponsiveContainer width="100%" height={260}>
        {granularity === 'month' ? (
          <LineChart {...common}>
            {axes}
            <Line type="monotone" dataKey="compare" name={compareName} stroke={COMPARE_COLOR} strokeDasharray="4 4" dot={false} />
            <Line type="monotone" dataKey="current" name="This period" stroke={color} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <BarChart {...common}>
            {axes}
            {compareName && <Bar dataKey="compare" name={compareName} fill={COMPARE_COLOR} radius={[4, 4, 0, 0]} />}
            <Bar dataKey="current" name="This period" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    );
  }

  return (
    <Card
      header={(
        <CardHeader
          title="Volume trend"
          supportingText={loading || error ? undefined : trendHeadline(trend, metric)}
          actions={actions}
        />
      )}
    >
      {body}
    </Card>
  );
}

export default BookingTrendCard;
